import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle, IdCard, ImageOff, Layers, Loader2, LocateFixed, MapPinOff, Palette, Save, Sparkles, TriangleAlert,
} from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import {
  COLOR_RE, DEFAULT_COLOR, ISO_RE, MAX_BLOCK_COORD, NAME_HINT, NAME_RE, SLUG_HINT, SLUG_RE, fmtDate, toIdentifier,
} from '../constants';
import type { Country, CountryInput, GeoRegion } from '../worlds.types';
import { ColorField } from './ColorField';
import { FlagEditor } from './FlagEditor';
import { FlagThumb } from './FlagThumb';
import { Modal } from './Modal';
import { NamePair } from './NamePair';
import { ColorChip, StatusBadge, cx } from './ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import cs from './countries.module.scss';

// ============================================================
// Création / édition d'un pays
// ============================================================
// En édition, seul le DIFF part en PATCH : renvoyer un champ inchangé ferait monter la version
// des pays pour rien (et relancer la synchro du roster). La création fait un seul POST, drapeau
// compris : pas d'état intermédiaire « pays créé sans drapeau ».
//
// Centre : changer X ou Z vide Y (sol recalculé en jeu) mais garde yaw et pitch, même règle que le
// glisser sur la carte. Le PATCH d'un déplacement porte donc toujours Y, yaw et pitch : sans eux,
// le satellite remettrait l'orientation à null.
//
// Noms EN (traduction locale, sans service externe) : tant que le champ EN est vide ou encore
// rempli automatiquement, il reprend le FR tapé dans cette modale (normalisé en identifiant) après
// une courte pause, marqué « repris du FR, à relire ». Une saisie dans le champ EN arrête le suivi.
// ============================================================

interface Form {
  slug: string;
  slugEn: string;
  empireNameFr: string;
  empireNameEn: string;
  color: string;
  centerX: string;
  centerY: string;
  centerZ: string;
  centerYaw: string;
  centerPitch: string;
  isoCode: string;
  geoRegionId: string;
  enabled: boolean;
  flag: string | null;
}

type FrKey = 'slug' | 'empireNameFr';
type EnKey = 'slugEn' | 'empireNameEn';
const PAIRS: readonly [FrKey, EnKey][] = [['slug', 'slugEn'], ['empireNameFr', 'empireNameEn']];

/** Pause de frappe avant de reprendre le FR dans l'EN. */
const AUTO_EN_DELAY_MS = 800;

const s = (v: number | null | undefined) => (v == null ? '' : String(v));

function toForm(c: Country | null): Form {
  return {
    slug: c?.slug ?? '',
    slugEn: c?.slugEn ?? '',
    empireNameFr: c?.empireNameFr ?? '',
    empireNameEn: c?.empireNameEn ?? '',
    color: c?.color ?? DEFAULT_COLOR,
    centerX: s(c?.centerX),
    centerY: s(c?.centerY),
    centerZ: s(c?.centerZ),
    centerYaw: s(c?.centerYaw),
    centerPitch: s(c?.centerPitch),
    isoCode: c?.isoCode ?? '',
    geoRegionId: c?.geoRegionId ?? '',
    enabled: c?.enabled ?? true,
    flag: c?.flag ?? null,
  };
}

type Errors = Partial<Record<keyof Form, string>>;

const isInt = (v: string) => /^-?\d+$/.test(v.trim());
const isNum = (v: string) => v.trim() !== '' && Number.isFinite(Number(v.trim()));

// Les noms sont validés et envoyés NORMALISÉS (toIdentifier), comme au blur du champ : Entrée sans
// quitter le champ enregistre la même valeur, et la longueur maximale porte sur le nom retenu.
function validate(f: Form): Errors {
  const e: Errors = {};
  const slug = toIdentifier(f.slug);
  if (!slug) e.slug = 'Nom requis';
  else if (!SLUG_RE.test(slug)) e.slug = slug.length > 17 ? `${SLUG_HINT} (« ${slug} » en fait ${slug.length})` : SLUG_HINT;
  for (const k of ['slugEn', 'empireNameFr', 'empireNameEn'] as const) {
    const v = toIdentifier(f[k]);
    if (v && !NAME_RE.test(v)) e[k] = v.length > 32 ? `${NAME_HINT} (ici ${v.length})` : NAME_HINT;
  }
  if (!COLOR_RE.test(f.color)) e.color = 'Format #RRGGBB';
  const hasX = f.centerX.trim() !== '';
  const hasZ = f.centerZ.trim() !== '';
  if (hasX !== hasZ) e[hasX ? 'centerZ' : 'centerX'] = 'X et Z se renseignent ensemble';
  for (const k of ['centerX', 'centerZ'] as const) {
    if (f[k].trim() && (!isInt(f[k]) || Math.abs(Number(f[k])) > MAX_BLOCK_COORD)) e[k] = `Entier, |v| ≤ ${MAX_BLOCK_COORD.toLocaleString('fr-FR')}`;
  }
  if (f.centerY.trim() && (!isInt(f.centerY) || Number(f.centerY) < -64 || Number(f.centerY) > 320)) e.centerY = 'Entier entre -64 et 320';
  if (f.centerYaw.trim() && (!isNum(f.centerYaw) || Math.abs(Number(f.centerYaw)) > 360)) e.centerYaw = 'Nombre entre -360 et 360';
  if (f.centerPitch.trim() && (!isNum(f.centerPitch) || Math.abs(Number(f.centerPitch)) > 90)) e.centerPitch = 'Nombre entre -90 et 90';
  if ((f.centerY.trim() || f.centerYaw.trim() || f.centerPitch.trim()) && !hasX && !hasZ) e.centerY = 'Sans X et Z, Y, yaw et pitch restent vides';
  if (f.isoCode.trim() && !ISO_RE.test(f.isoCode.trim())) e.isoCode = '1 à 3 lettres';
  return e;
}

/** Valeurs envoyées au serveur (chaînes vides → null). */
function toInput(f: Form): Required<CountryInput> {
  const str = (v: string) => (v.trim() ? v.trim() : null);
  const int = (v: string) => (v.trim() ? Number.parseInt(v.trim(), 10) : null);
  const num = (v: string) => (v.trim() ? Number(v.trim()) : null);
  return {
    slug: toIdentifier(f.slug),
    slugEn: str(toIdentifier(f.slugEn)),
    empireNameFr: str(toIdentifier(f.empireNameFr)),
    empireNameEn: str(toIdentifier(f.empireNameEn)),
    color: f.color.toUpperCase(),
    centerX: int(f.centerX),
    centerY: int(f.centerY),
    centerZ: int(f.centerZ),
    centerYaw: num(f.centerYaw),
    centerPitch: num(f.centerPitch),
    isoCode: f.isoCode.trim() ? f.isoCode.trim().toLowerCase() : null,
    geoRegionId: f.geoRegionId || null,
    flag: f.flag,
    enabled: f.enabled,
  };
}

export function CountryModal({ country, geoRegions, onClose, onSaved, readOnly = false }: {
  /** null = création. */
  country: Country | null;
  geoRegions: GeoRegion[];
  onClose: () => void;
  onSaved: (c: Country) => void;
  readOnly?: boolean;
}) {
  const [form, setForm] = useState<Form>(() => toForm(country));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Erreurs affichées après une saisie dans le champ, ou après une tentative d'enregistrement.
  const [touched, setTouched] = useState<ReadonlySet<keyof Form>>(() => new Set());
  const [submitted, setSubmitted] = useState(false);
  // Noms EN remplis depuis le FR (traduction locale), jusqu'à la première saisie manuelle.
  const [autoEn, setAutoEn] = useState<Record<EnKey, boolean>>({ slugEn: false, empireNameEn: false });
  const frTyped = useRef<Record<FrKey, boolean>>({ slug: false, empireNameFr: false });

  const initial = useMemo(() => toForm(country), [country]);
  const original = useMemo(() => toInput(initial), [initial]);
  const countries = useWorldsStore((st) => st.countries);
  const errors = validate(form);
  // Unicité sans tenir compte de la casse, comme le serveur (qui reste l'autorité : 409).
  const slugLower = toIdentifier(form.slug).toLowerCase();
  if (!errors.slug && countries.some((c) => c.id !== country?.id && c.slug.toLowerCase() === slugLower)) {
    errors.slug = 'Nom déjà pris par un autre pays (casse ignorée)';
  }
  const valid = Object.keys(errors).length === 0;
  const errOf = (k: keyof Form): string | null => ((submitted || touched.has(k)) ? errors[k] ?? null : null);

  const touch = (k: keyof Form) => setTouched((t) => (t.has(k) ? t : new Set(t).add(k)));
  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    touch(k);
    setForm((f) => ({ ...f, [k]: v }));
  };

  // Traduction locale : l'EN suit le FR tapé ici tant qu'il est vide ou encore automatique. Les
  // refs donnent au minuteur l'état courant sans le relancer à chaque rendu.
  const formRef = useRef(form);
  formRef.current = form;
  const autoEnRef = useRef(autoEn);
  autoEnRef.current = autoEn;
  useEffect(() => {
    if (readOnly) return undefined;
    const timer = window.setTimeout(() => {
      const f = formRef.current;
      const patch: Partial<Record<EnKey, string>> = {};
      const autoPatch: Partial<Record<EnKey, boolean>> = {};
      for (const [fr, en] of PAIRS) {
        if (!frTyped.current[fr]) continue;
        // Un EN saisi à la main n'est jamais écrasé.
        if (f[en].trim() && !autoEnRef.current[en]) continue;
        const next = toIdentifier(f[fr]);
        if (next !== f[en]) patch[en] = next;
        autoPatch[en] = next !== '';
      }
      if (Object.keys(patch).length > 0) setForm((c) => ({ ...c, ...patch }));
      if (Object.keys(autoPatch).length > 0) setAutoEn((a) => ({ ...a, ...autoPatch }));
    }, AUTO_EN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [form.slug, form.empireNameFr, readOnly]);

  const setFr = (k: FrKey, v: string) => {
    frTyped.current[k] = true;
    set(k, v);
  };

  const setEn = (fr: FrKey, en: EnKey, v: string) => {
    // « Reprendre le FR » (NamePair) : champ vide rempli d'un coup avec le FR normalisé.
    const copied = !form[en].trim() && !!v && v === toIdentifier(form[fr]);
    setAutoEn((a) => ({ ...a, [en]: copied }));
    set(en, v);
  };

  /**
   * Changer X ou Z : la position précise posée en jeu ne vaut plus. Y se vide (sol recalculé en
   * jeu) s'il n'a pas été retouché ici ; yaw et pitch restent. Plus de X ni de Z : plus de centre,
   * Y, yaw et pitch se vident aussi (le satellite les efface de toute façon).
   */
  const setCenterXZ = (k: 'centerX' | 'centerZ', v: string) => {
    touch(k);
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (!next.centerX.trim() && !next.centerZ.trim()) {
        next.centerY = '';
        next.centerYaw = '';
        next.centerPitch = '';
      } else if (f.centerY === initial.centerY) {
        next.centerY = '';
      }
      return next;
    });
  };

  const cur = useMemo(() => toInput(form), [form]);
  const diff = useMemo(() => {
    const out: CountryInput = {};
    (Object.keys(cur) as (keyof CountryInput)[]).forEach((k) => {
      if (cur[k] !== original[k]) (out as Record<string, unknown>)[k] = cur[k];
    });
    // Déplacement : Y, yaw et pitch explicites, sinon le satellite remet l'orientation à null.
    if ((hasKey(out, 'centerX') || hasKey(out, 'centerZ')) && cur.centerX != null) {
      out.centerY = cur.centerY;
      out.centerYaw = cur.centerYaw;
      out.centerPitch = cur.centerPitch;
    }
    return out;
  }, [cur, original]);

  const renamed = !!country && diff.slug !== undefined;
  // Saisie pas encore normalisée (espaces, accents) : le nom retenu, avant de quitter le champ.
  const slugPreview = form.slug.trim() && toIdentifier(form.slug) !== form.slug.trim() ? toIdentifier(form.slug) : null;
  const moved = !!country && (hasKey(diff, 'centerX') || hasKey(diff, 'centerZ'));
  const dirty = Object.keys(diff).length > 0;

  const save = async () => {
    if (readOnly || busy) return;
    if (!valid) {
      setSubmitted(true);
      setError('Corrige les champs signalés en rouge.');
      return;
    }
    setBusy(true);
    setError(null);
    let res;
    if (country) {
      if (!dirty) { setBusy(false); onClose(); return; }
      res = await api.patchCountry(country.id, diff);
    } else {
      // Création : on n'envoie pas les champs vides (le serveur applique ses défauts).
      const clean: CountryInput = {};
      (Object.keys(cur) as (keyof CountryInput)[]).forEach((k) => {
        if (cur[k] !== null) (clean as Record<string, unknown>)[k] = cur[k];
      });
      res = await api.createCountry(clean);
    }
    setBusy(false);
    if (!res.data) { setError(res.error ?? 'Enregistrement en échec'); return; }
    toast.success(country ? `Pays ${res.data.slug} mis à jour` : `Pays ${res.data.slug} créé`);
    onSaved(res.data);
    onClose();
  };

  const clearCenter = () => {
    ['centerX', 'centerY', 'centerZ', 'centerYaw', 'centerPitch'].forEach((k) => touch(k as keyof Form));
    setForm((f) => ({ ...f, centerX: '', centerY: '', centerZ: '', centerYaw: '', centerPitch: '' }));
  };

  const field = (k: keyof Form, label: ReactNode, input: ReactNode, hint?: ReactNode) => {
    const err = errOf(k);
    return (
      <label className={styles.field} style={{ minWidth: 0 }}>
        <span className={styles.label}>{label}</span>
        {input}
        {err ? <span className={ws.fieldError}>{err}</span> : hint ? <span className={ws.fieldHint}>{hint}</span> : null}
      </label>
    );
  };

  const num = (k: 'centerX' | 'centerY' | 'centerZ' | 'centerYaw' | 'centerPitch', placeholder?: string) => (
    <input className={cx(styles.input, cs.monoInput)} inputMode={k === 'centerYaw' || k === 'centerPitch' ? 'decimal' : 'numeric'}
      value={form[k]} disabled={readOnly} placeholder={placeholder} aria-invalid={!!errOf(k) || undefined}
      onChange={(e) => (k === 'centerX' || k === 'centerZ' ? setCenterXZ(k, e.target.value) : set(k, e.target.value))} />
  );

  const enHint = (en: EnKey, fallback: ReactNode) => (autoEn[en] && form[en].trim() ? (
    <span className={cs.autoHint}>
      <StatusBadge tone="warn" icon={Sparkles}>repris du FR</StatusBadge>
      À relire : ce nom est lu par les joueurs anglophones.
    </span>
  ) : fallback);

  const region = form.geoRegionId ? geoRegions.find((r) => r.id === form.geoRegionId) : undefined;
  const hasCenter = form.centerX.trim() !== '' && form.centerZ.trim() !== '';

  const subtitle = country
    ? `idx ${country.idx} · modifié le ${fmtDate(country.updatedAt)}${country.updatedBy ? ` par ${country.updatedBy}` : ''}`
    : 'Nom fondable et couleur requis ; drapeau, f home et région facultatifs.';

  return (
    <Modal size="xl" busy={busy} onClose={onClose} onSubmit={readOnly ? undefined : () => void save()}
      title={country ? `${readOnly ? 'Pays' : 'Modifier'} ${country.slug}` : 'Nouveau pays'}
      icon={country ? <FlagThumb flag={form.flag} width={40} /> : <ColorChip color={COLOR_RE.test(form.color) ? form.color : DEFAULT_COLOR} size={24} />}
      subtitle={subtitle}
      footer={(
        <>
          {error && <span className={ws.footError}><AlertTriangle size={13} aria-hidden /> {error}</span>}
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>
            {readOnly ? 'Fermer' : 'Annuler'}
          </button>
          {!readOnly && (
            <button type="submit" className={styles.primaryBtn} disabled={busy}>
              {busy ? <Loader2 size={14} className={ws.spinning} aria-hidden /> : <Save size={14} aria-hidden />}
              {country ? 'Enregistrer' : 'Créer le pays'}
            </button>
          )}
        </>
      )}>

      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><IdCard size={14} aria-hidden /> Identité</h3>
        <NamePair label="Nom fondable" required={!readOnly} mono disabled={readOnly} normalize={toIdentifier} copyFromFr
          fr={{
            value: form.slug, onChange: (v) => setFr('slug', v), error: errOf('slug'), maxLength: 17,
            placeholder: 'Afghanistan', autoFocus: !country && !readOnly,
            hint: slugPreview
              ? <>Sera enregistré : <strong className={ws.mono}>{slugPreview}</strong> ({slugPreview.length} caractères sur 17).</>
              : renamed
                ? 'Renommage refusé si le pays est fondé en jeu.'
                : 'Nom proposé à la fondation (/f create) : 3 à 17 caractères, sans espace ni accent.',
          }}
          en={{
            value: form.slugEn, onChange: (v) => setEn('slug', 'slugEn', v), error: errOf('slugEn'), maxLength: 32,
            placeholder: 'Afghanistan', hint: enHint('slugEn', 'Optionnel : vide, le jeu reprend le nom FR.'),
          }} />
        <NamePair label="Nom d'empire" mono disabled={readOnly} normalize={toIdentifier} copyFromFr
          hint="Optionnel : nom porté par le pays une fois devenu un empire."
          fr={{
            value: form.empireNameFr, onChange: (v) => setFr('empireNameFr', v), error: errOf('empireNameFr'),
            maxLength: 32, placeholder: 'EmpireAfghan',
          }}
          en={{
            value: form.empireNameEn, onChange: (v) => setEn('empireNameFr', 'empireNameEn', v), error: errOf('empireNameEn'),
            maxLength: 32, placeholder: 'AfghanEmpire', hint: enHint('empireNameEn', undefined),
          }} />
      </section>

      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><Palette size={14} aria-hidden /> Apparence</h3>
        <ColorField label="Couleur sur la carte" value={form.color} disabled={readOnly} error={errOf('color')}
          suggestions={readOnly ? [] : undefined} onChange={(v) => set('color', v)} />
        <div className={cs.fieldBlock}>
          <div className={cs.flagHead}>
            <span className={styles.label}>Drapeau par défaut</span>
            <span className={ws.fieldHint}>Grille 26 x 13 (format NationsGlory)</span>
            {form.flag == null
              ? <StatusBadge tone="off" icon={ImageOff} title="Le jeu affiche un drapeau blanc">aucun drapeau : blanc en jeu</StatusBadge>
              : initial.flag !== form.flag && country ? <StatusBadge tone="info">modifié</StatusBadge> : null}
          </div>
          <FlagEditor value={form.flag} readOnly={readOnly} nullable onChange={(b64) => set('flag', b64)} />
        </div>
      </section>

      <section className={ws.formSection}>
        <div className={cs.sectionHead}>
          <h3 className={ws.formSectionTitle}><LocateFixed size={14} aria-hidden /> Point d'apparition (f home)</h3>
          {!hasCenter && <StatusBadge tone="warn" icon={TriangleAlert}>aucun f home : pays non fondable</StatusBadge>}
        </div>
        <div className={ws.formGrid5}>
          {field('centerX', 'X', num('centerX', 'aucun'))}
          {field('centerY', 'Y', num('centerY', 'sol'), 'Vide : sol calculé en jeu')}
          {field('centerZ', 'Z', num('centerZ', 'aucun'))}
          {field('centerYaw', 'Yaw', num('centerYaw'))}
          {field('centerPitch', 'Pitch', num('centerPitch'))}
        </div>
        {country?.centerInside === false && !moved && hasCenter && (
          <span className={cs.warnLine}>
            <TriangleAlert size={13} aria-hidden /> Ce f home est hors des chunks du pays sur le niveau affiché.
          </span>
        )}
        <div className={cs.centerHint}>
          {!readOnly && (
            <button type="button" className={styles.secondaryBtn} onClick={clearCenter}
              disabled={!form.centerX && !form.centerZ && !form.centerY && !form.centerYaw && !form.centerPitch}>
              <MapPinOff size={13} aria-hidden /> Vider le centre
            </button>
          )}
          <span>
            X et Z vides : aucun f home, le pays n'est pas proposé à la fondation jusqu'à un <code>/f setdhome</code> en
            jeu, qui pose aussi Y et l'orientation. Changer X ou Z vide Y (sol recalculé en jeu) ; yaw et pitch sont gardés.
          </span>
        </div>
      </section>

      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><Layers size={14} aria-hidden /> Rattachement</h3>
        <div className={cs.attachGrid}>
          {field('isoCode', 'Code ISO', (
            <input className={cx(styles.input, cs.isoInput)} value={form.isoCode} disabled={readOnly} maxLength={3}
              placeholder="afg" aria-invalid={!!errOf('isoCode') || undefined}
              onChange={(e) => set('isoCode', e.target.value.toLowerCase())} />
          ), '1 à 3 lettres, optionnel')}
          {field('geoRegionId', 'Géo-région', (
            <span className={cs.withChip}>
              {region ? <ColorChip color={region.color} size={16} title={region.color} /> : null}
              <select className={styles.input} value={form.geoRegionId} disabled={readOnly || geoRegions.length === 0}
                onChange={(e) => set('geoRegionId', e.target.value)}>
                <option value="">Aucune région</option>
                {geoRegions.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.slug})</option>)}
              </select>
            </span>
          ), geoRegions.length === 0
            ? 'Aucune région : crée-en une dans l\'onglet Régions.'
            : region ? `Pétrole et gaz de la région ${region.name}.` : 'Sans région : pas de stock de pétrole ni de gaz.')}
          <div className={styles.field} style={{ minWidth: 0 }}>
            <span className={styles.label}>Actif</span>
            <label className={ws.checkLine}>
              <input type="checkbox" checked={form.enabled} disabled={readOnly} onChange={(e) => set('enabled', e.target.checked)} />
              {form.enabled ? 'Proposé à la fondation et affiché' : 'Désactivé'}
            </label>
            <p className={cs.checkHint}>
              Décoché : retiré du roster fondable, frontières masquées. Chunks peints et faction déjà installée non touchés.
            </p>
          </div>
        </div>
      </section>
    </Modal>
  );
}

/** La clé est-elle présente dans le corps (même à null) ? */
function hasKey(o: CountryInput, k: keyof CountryInput): boolean {
  return Object.prototype.hasOwnProperty.call(o, k);
}
